import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Clock, LogIn, LogOut, CheckCircle, UserCheck, ChevronLeft, Lock, AlertCircle } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const VolunteerClockIn = () => {
  const [volunteers, setVolunteers] = useState([]);
  const [selected, setSelected] = useState(null);
  const [status, setStatus] = useState(null);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [password, setPassword] = useState('');

  useEffect(() => {
    fetch(`${API}/api/volunteers/public/list`).then(r => r.json()).then(setVolunteers).catch(() => {});
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  const checkStatus = useCallback(async (id) => {
    try {
      const r = await fetch(`${API}/api/volunteers/clock-status/${id}`);
      const data = await r.json();
      setStatus(data);
    } catch { setStatus(null); }
  }, []);

  useEffect(() => {
    if (selected) { checkStatus(selected.id); setPassword(''); setError(''); }
  }, [selected, checkStatus]);

  const clockIn = async () => {
    setLoading(true);
    setError('');
    try {
      const r = await fetch(`${API}/api/volunteers/clock-in`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ volunteer_id: selected.id, password })
      });
      if (!r.ok) {
        const err = await r.json();
        setError(err.detail || 'Clock-in failed');
        setLoading(false);
        return;
      }
      const data = await r.json();
      setMessage(data.message || 'Clocked in!');
      setPassword('');
      checkStatus(selected.id);
    } catch { setError('Error clocking in'); }
    setLoading(false);
    setTimeout(() => setMessage(''), 4000);
  };

  const clockOut = async () => {
    setLoading(true);
    setError('');
    try {
      const r = await fetch(`${API}/api/volunteers/clock-out`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ volunteer_id: selected.id, password })
      });
      if (!r.ok) {
        const err = await r.json();
        setError(err.detail || 'Clock-out failed');
        setLoading(false);
        return;
      }
      const data = await r.json();
      setMessage(data.message || 'Clocked out!');
      setPassword('');
      checkStatus(selected.id);
    } catch { setError('Error clocking out'); }
    setLoading(false);
    setTimeout(() => setMessage(''), 4000);
  };

  const formatTime = (d) => d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  const formatDate = (d) => d.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });

  return (
    <div className="min-h-screen bg-gradient-to-br from-violet-950 via-purple-900 to-violet-950 flex flex-col items-center justify-center p-4"
      data-testid="volunteer-clockin-page">

      {/* Live Clock */}
      <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }}
        className="text-center mb-6">
        <p className="text-amber-400 text-sm font-medium tracking-widest uppercase">Volunteer Ministry</p>
        <h1 className="text-5xl sm:text-6xl font-black text-white tabular-nums mt-2" data-testid="live-clock">
          {formatTime(currentTime)}
        </h1>
        <p className="text-slate-400 mt-1">{formatDate(currentTime)}</p>
      </motion.div>

      <AnimatePresence mode="wait">
        {!selected ? (
          /* Volunteer Selection — shows initials only */
          <motion.div key="select" initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }}
            className="w-full max-w-md" data-testid="volunteer-select-list">
            <div className="bg-white/10 backdrop-blur-xl rounded-2xl border border-white/20 p-6">
              <div className="flex items-center gap-3 mb-5">
                <div className="p-2.5 rounded-xl bg-amber-500/20"><UserCheck className="w-5 h-5 text-amber-400" /></div>
                <div>
                  <h2 className="text-lg font-bold text-white">Select Your Name</h2>
                  <p className="text-slate-400 text-xs">Tap your initials to clock in or out</p>
                </div>
              </div>
              <div className="space-y-2 max-h-[50vh] overflow-y-auto">
                {volunteers.map(v => (
                  <motion.button key={v.id} onClick={() => setSelected(v)}
                    whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}
                    data-testid={`select-volunteer-${v.id}`}
                    className="w-full text-left p-4 rounded-xl bg-white/5 hover:bg-amber-500/20 border border-white/10 hover:border-amber-500/40 transition-all">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-bold text-white text-lg tracking-wider">{v.name}</p>
                        <p className="text-xs text-slate-400">{v.role} | {v.department}</p>
                      </div>
                      {v.has_password && <Lock className="w-4 h-4 text-amber-400/60" />}
                    </div>
                  </motion.button>
                ))}
                {volunteers.length === 0 && (
                  <p className="text-center text-slate-500 py-8">No volunteers registered</p>
                )}
              </div>
            </div>
          </motion.div>
        ) : (
          /* Clock In / Out */
          <motion.div key="clock" initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }}
            className="w-full max-w-md" data-testid="clock-panel">
            <div className="bg-white/10 backdrop-blur-xl rounded-2xl border border-white/20 p-6">
              <button onClick={() => { setSelected(null); setStatus(null); setMessage(''); setPassword(''); setError(''); }}
                className="flex items-center gap-1 text-slate-400 hover:text-white text-sm mb-4 transition-colors">
                <ChevronLeft className="w-4 h-4" /> Back
              </button>

              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-amber-500/20 rounded-full flex items-center justify-center mx-auto mb-3">
                  <UserCheck className="w-8 h-8 text-amber-400" />
                </div>
                <h2 className="text-2xl font-bold text-white tracking-wider">{selected.name}</h2>
                <p className="text-slate-400 text-sm">{selected.role} | {selected.department}</p>
              </div>

              {/* Status */}
              {status && (
                <div className={`rounded-xl p-4 mb-5 border ${
                  status.clocked_in && !status.clocked_out
                    ? 'bg-emerald-500/10 border-emerald-500/30'
                    : status.clocked_out
                      ? 'bg-blue-500/10 border-blue-500/30'
                      : 'bg-slate-500/10 border-slate-500/30'
                }`} data-testid="clock-status">
                  {status.clocked_in ? (
                    <div className="text-center">
                      <p className="text-sm text-slate-300 mb-1">{status.session_name}</p>
                      <div className="flex items-center justify-center gap-4">
                        <div>
                          <p className="text-xs text-slate-400">Clock In</p>
                          <p className="text-lg font-bold text-emerald-400" data-testid="display-clock-in">{status.clock_in_time}</p>
                        </div>
                        {status.clock_out_time && (
                          <div>
                            <p className="text-xs text-slate-400">Clock Out</p>
                            <p className="text-lg font-bold text-blue-400" data-testid="display-clock-out">{status.clock_out_time}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  ) : (
                    <p className="text-center text-slate-400 text-sm">Not clocked in today</p>
                  )}
                </div>
              )}

              {/* Password input — shown when volunteer has a password */}
              {selected.has_password && !status?.clocked_out && (
                <div className="mb-4">
                  <label className="text-xs text-slate-400 block mb-1.5">Enter your password to continue</label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                    <input
                      type="password"
                      value={password}
                      onChange={e => { setPassword(e.target.value); setError(''); }}
                      onKeyDown={e => {
                        if (e.key === 'Enter') {
                          if (!status?.clocked_in) clockIn();
                          else if (!status?.clocked_out) clockOut();
                        }
                      }}
                      placeholder="Password"
                      data-testid="clockin-password-input"
                      className="w-full pl-10 pr-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-slate-500 focus:border-amber-400 outline-none"
                    />
                  </div>
                </div>
              )}

              {/* Error message */}
              {error && (
                <div className="mb-4 p-3 rounded-xl bg-red-500/20 border border-red-500/30 flex items-center gap-2" data-testid="clockin-error">
                  <AlertCircle className="w-4 h-4 text-red-400 shrink-0" />
                  <p className="text-red-300 text-sm">{error}</p>
                </div>
              )}

              {/* Action Buttons */}
              <div className="space-y-3">
                {(!status?.clocked_in) && (
                  <motion.button onClick={clockIn} disabled={loading || (selected.has_password && !password)}
                    whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}
                    data-testid="clock-in-btn"
                    className="w-full py-4 bg-emerald-500 hover:bg-emerald-600 text-white rounded-xl font-bold text-lg flex items-center justify-center gap-3 disabled:opacity-50 transition-colors">
                    <LogIn className="w-6 h-6" /> Clock In
                  </motion.button>
                )}
                {status?.clocked_in && !status?.clocked_out && (
                  <motion.button onClick={clockOut} disabled={loading || (selected.has_password && !password)}
                    whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}
                    data-testid="clock-out-btn"
                    className="w-full py-4 bg-blue-500 hover:bg-blue-600 text-white rounded-xl font-bold text-lg flex items-center justify-center gap-3 disabled:opacity-50 transition-colors">
                    <LogOut className="w-6 h-6" /> Clock Out
                  </motion.button>
                )}
                {status?.clocked_out && (
                  <div className="text-center py-4">
                    <CheckCircle className="w-10 h-10 text-emerald-400 mx-auto mb-2" />
                    <p className="text-emerald-400 font-bold">Attendance Complete</p>
                    <p className="text-slate-400 text-sm">Thank you for your service today!</p>
                  </div>
                )}
              </div>

              {/* Toast */}
              <AnimatePresence>
                {message && (
                  <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                    className="mt-4 p-3 rounded-xl bg-amber-500/20 border border-amber-500/30 text-center">
                    <p className="text-amber-300 font-medium text-sm">{message}</p>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Footer */}
      <p className="text-slate-600 text-xs mt-8">Celestial Church of Christ &middot; Valley of Berachah Parish</p>
    </div>
  );
};

export default VolunteerClockIn;
